if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        /* START BODILY MODIFICATION */
        selectedOptionValues = this.resolveSelectedOptionValues(target, selectedOptionValues);
        /* END BODILY MODIFICATION */

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      /* START BODILY MODIFICATION: resolve option combinations that don't exist */
      // When a shopper changes one option (e.g. Fit) and their other current selections don't exist
      // together with it (e.g. Soft Hold + Dusk), swap the other selections for the closest variant
      // that does exist: in-stock first, then the one keeping the most of the current selections.
      // The option the shopper just clicked is never changed. Any problem falls back to default behavior.
      // Needs [data-all-variant-data] and [data-option-value-ids] (snippets/product-variant-picker.liquid).
      resolveSelectedOptionValues(target, selectedOptionValues) {
        try {
          const picker = target?.closest?.('variant-selects');
          if (!picker || !Array.isArray(selectedOptionValues)) return selectedOptionValues;

          const variants = JSON.parse(picker.querySelector('[data-all-variant-data]')?.textContent || '[]');
          const optionMap = JSON.parse(picker.querySelector('[data-option-value-ids]')?.textContent || '[]');
          if (!variants.length || !optionMap.length || selectedOptionValues.length !== optionMap.length) {
            return selectedOptionValues;
          }

          const findValue = (id) => {
            for (let position = 0; position < optionMap.length; position++) {
              const match = optionMap[position].find((v) => String(v.id) === String(id));
              if (match) return { position, value: match.value };
            }
            return null;
          };

          const selected = new Array(optionMap.length);
          for (const id of selectedOptionValues) {
            const found = findValue(id);
            if (!found) return selectedOptionValues;
            selected[found.position] = found.value;
          }
          for (let i = 0; i < selected.length; i++) {
            if (selected[i] === undefined) return selectedOptionValues;
          }

          // Combination exists (in stock or not): nothing to fix
          if (variants.some((v) => v.options.every((o, i) => o === selected[i]))) return selectedOptionValues;

          const changed = findValue(target.dataset.optionValueId);
          if (!changed) return selectedOptionValues;

          let best = null;
          let bestScore = -1;
          variants.forEach((v) => {
            if (v.options[changed.position] !== changed.value) return;
            let score = v.available ? 100 : 0;
            v.options.forEach((o, i) => {
              if (i !== changed.position && o === selected[i]) score += 1;
            });
            if (score > bestScore) {
              best = v;
              bestScore = score;
            }
          });
          if (!best) return selectedOptionValues;

          const resolved = best.options.map((value, i) => optionMap[i]?.find((v) => v.value === value)?.id);
          if (resolved.some((id) => id === undefined || id === null)) return selectedOptionValues;
          return resolved.map(String);
        } catch (e) {
          console.error('Error resolving option values', e);
          return selectedOptionValues;
        }
      }
      /* END BODILY MODIFICATION */

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );
          
          /* START DAWN MODIFICATION */
          updateSourceFromDestination('price-mobile');
          updateSourceFromDestination('ships-by-text');
          
          updateSourceFromDestination('Presell-progress');
          
          if (variant && variant.options) {
            
            try {
              
              let variantData = html.querySelector('variant-selects [data-all-variant-data]')?.innerHTML;
              variantData = !!variantData ? JSON.parse(variantData) : [];
              
              const selected_options = variant.options;
              
              // A value is crossed out only if no in-stock variant has that value while
              // keeping every OTHER currently selected option the same.
              // Works for any number of options in any order (e.g. Fit / Color / Size).
              const isValueAvailable = (position, value) =>
                variantData.some((v) =>
                  v.available &&
                  v.options[position] === value &&
                  v.options.every((o, j) => j === position || o === selected_options[j])
                );
              
              // Inputs are named "<Option name>-<position>" (see product-variant-options.liquid).
              // Scoped to this product's variant pickers so bundle pickers aren't affected.
              selected_options.forEach((_, position) => {
                this.querySelectorAll(`variant-selects input[name$="-${position + 1}"]`).forEach((el) => {
                  el.classList.toggle('disabled', !isValueAvailable(position, el.value));
                });
              });
            } catch(e) {
              console.error("Error updating variant disable status", e) 
            }
          }
          
          /* END DAWN MODIFICATION */

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}





/* START DAWN MODIFICATION */
if (!customElements.get('bundle-option-selects')) {
  
    class BundleOptionSelects extends HTMLElement {
      constructor() {
        super();
      }
      
      connectedCallback() {
        this.addEventListener('change', this.onVariantChange);
        this.insertAdjacentHTML(
          'beforeend',
          `<input type="hidden" form="${this.dataset.formId}" name="properties[${this.dataset.bundleSelection}]"/>`
        );
    
        // init
        this.onVariantChange();
      }
    
      getSelectedVariant() {
        return this.currentVariant;
      }
    
      onVariantChange() {
        this.updateOptions();
        this.updateMasterId();
        this.updateVariantStatuses();
    
        if (this.currentVariant) {
          this.updateMedia();
        }
      }
    
      updateOptions() {
        this.options = Array.from(this.querySelectorAll('fieldset'), (element) => {
          return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
        });
      }
    
      updateMasterId() {
        this.currentVariant = this.getVariantData().find((variant) => {
          return !variant.options
            .map((option, index) => {
              return this.options[index] === option;
            })
            .includes(false);
        });

        if (!this.currentVariant) return; // ADD THIS LINE

        this.querySelector('input[name*="properties"]').value = this.currentVariant.name;
        this.updateMedia();
        // sync Color: ____ value
        // Finds the Color fieldset's position so option order doesn't matter (e.g. Fit / Color / Size)
          const colorInput = this.querySelector('input[name*=Color]');
          if (colorInput) {
            const colorFieldset = colorInput.closest('fieldset');
            const colorIndex = Array.from(this.querySelectorAll('fieldset')).indexOf(colorFieldset);
            const colorValue = (colorIndex >= 0 && this.currentVariant.options?.[colorIndex]) || this.currentVariant.option1;
            let form_label = colorFieldset.querySelector('.form__label');
            if (form_label) {
              if (form_label.innerText.includes('Color')) {
                form_label.innerHTML = 'Color: ' + colorValue;
              } else if (form_label.innerText.includes('Flavor')) {
                form_label.innerHTML = 'Flavor: ' + colorValue;
              }
            }
          }
    
        var event = new CustomEvent('BODILY__BUNDLE_SELECTION_UPDATED', {
          detail: {
            name: this.dataset.bundleSelection,
            selection: this.currentVariant,
          },
        });
    
        this.dispatchEvent(event, { bubbles: true });
      }
    
      updateMedia() {
        if (!this.currentVariant) return;
        if (!this.currentVariant.featured_media) return;

        this.querySelectorAll(`.bundle__image-preview`).forEach((el) => el.classList.remove('selected'));
        
        const mediaEl = this.querySelector(`.bundle__image-preview[data-variants*="${this.currentVariant.id}"]`);
        if (mediaEl) {
          mediaEl.classList.add('selected');
        }
      }
    
      updateVariantStatuses() {
        const selectedOptionOneVariants = this.variantData.filter(
          (variant) => this.querySelector(':checked').value === variant.option1
        );
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    
        inputWrappers.forEach((option, index) => {
          if (index === 0) return;
          const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
          const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
    
          const availableOptionInputsValue = selectedOptionOneVariants
            .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
            .map((variantOption) => variantOption[`option${index + 1}`]);
          this.setInputAvailability(optionInputs, availableOptionInputsValue);
        });
      }
    
      setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
          const value = element.getAttribute('value');
          const availableElement = availableValuesList.includes(value);
          if (element.tagName === 'INPUT') {
            element.classList.toggle('disabled', !availableElement);
          } else if (element.tagName === 'OPTION') {
            element.innerText = availableElement
              ? value
              : window.variantStrings.unavailable_with_option.replace('[value]', value);
          }
        });
      }
    
      getVariantData() {
        this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
        return this.variantData;
      }
    }
    
    customElements.define('bundle-option-selects', BundleOptionSelects);
    

}


if (!customElements.get('bundle-selections')) {

    class BundleSelections extends HTMLElement {
      constructor() {
        super();
      
      }
      
      async connectedCallback() {
        let _this = this;
        this.selections = [];
        this.submit_button = this.closest('.product__info-container').querySelector('#multi-atc-submit');
        this.submit_button.addEventListener('click', _this.onSubmit.bind(_this));

        // wait till all child selects have fully loaded before proceeding
        await Promise.all(
          Array.from(this.querySelectorAll('bundle-option-selects'))
            .map(el => customElements.whenDefined(el.localName))
        );

        this.bundle_selection_forms = this.querySelectorAll('bundle-option-selects');

        this.bundle_selection_forms.forEach((el) => {
          el.addEventListener('BODILY__BUNDLE_SELECTION_UPDATED', _this.onSelectionChange.bind(_this));
        });

        // Wait for all children to finish their connectedCallback and load variant data
        await Promise.all(
          Array.from(this.bundle_selection_forms).map(el => {
            return new Promise(resolve => {
              // If already initialized (variantData loaded), resolve immediately
              if (el.variantData) {
                resolve();
              } else {
                // Otherwise wait for first BODILY__BUNDLE_SELECTION_UPDATED event
                el.addEventListener('BODILY__BUNDLE_SELECTION_UPDATED', () => resolve(), { once: true });
              }
            });
          })
        );

        // Now safe to check OOS state on load
        this.onSelectionChange();
      }
    
      onSelectionChange(e) {


          // DEBUG - remove after testing
          // console.log('=== onSelectionChange fired ===');
          // Array.from(this.bundle_selection_forms || []).forEach((el, i) => {
          //   const variants = el.getVariantData();
          //   console.log(`Child ${i + 1}:`, el.dataset.bundleSelection);
          //   console.log(`  variant count: ${variants.length}`);
          //   console.log(`  all OOS: ${variants.length > 0 && variants.every(v => !v.available)}`);
          //   variants.forEach(v => console.log(`  - ${v.title}: available=${v.available}`));
          // });
         // END DEBUG
        // Check if any child product has ALL variants out of stock
        const has_oos_child = Array.from(this.bundle_selection_forms).some((el) => {
          const variants = el.getVariantData();
          return variants.length > 0 && variants.every((v) => !v.available);
        });

        this.selections = Array.from(this.bundle_selection_forms).map((el) => {
          let variant = el.getSelectedVariant();

          if (!variant || !variant.available) {
            return undefined;
          }

          return variant;
        });

        let is_valid = this.selections.filter((s) => s === undefined).length === 0;

        if (!is_valid || has_oos_child) {
          this.submit_button.setAttribute('disabled', '');
        } else {
          this.submit_button.removeAttribute('disabled');
        }

        if (has_oos_child) {
          this.submit_button.querySelector('span').innerText = 'Out of stock';
        } else {
          this.submit_button.querySelector('span').innerText = is_valid ? 'Add to bag' : 'Choose options';
        }

        // Legacy OOS flag override (kept for backward compat)
        if (this.submit_button.dataset.oos === 'true') {
          this.submit_button.querySelector('span').innerText = 'Out of stock';
        }
      }
      
      onSubmit(e) {
        e.preventDefault();
        
        let _this = this
        _this.submit_button.classList.add('loading')
        _this.submit_button.querySelector('.loading__spinner').classList.remove('hidden')
        
        let properties = {}
        
        let extended_data = {}
        try {
          // FIXED - merges all bundle product metafields into one lookup object
document.querySelectorAll("script.bundle__options-extended-data").forEach(script => {
  try {
    Object.assign(extended_data, JSON.parse(script.textContent.trim()));
  } catch {}
});
        } catch(e) {}
        
        if (_this.dataset.bundle === 'true') {
          let discount_percent = parseFloat(_this.dataset.discountPercent);
          
          let components_count = _this.selections.length;
          let discount_id = Date.now()
          properties = {
            '_parent_bundle': _this.dataset.parentTitle,
            '_bundle_discount': `${components_count}|${discount_id}|${discount_percent}`
          }
        }
        
        const formData = {
          'items': _this.selections.map(v => {
            let item_properties = { ...properties }
            
            try { 
              if (extended_data && extended_data[v.id]) {
                if (extended_data[v.id].atc_text) {
                  item_properties['Note'] = extended_data[v.id].atc_text
                }
                                
                try {
                  item_properties['_atc_source'] = location.pathname.split('/').filter(Boolean).pop();
                } catch(e){}

                /*
                Removed per Sean 12.15.25
                if (extended_data[v.id].ships_by) {
                  item_properties['Ships by'] = extended_data[v.id].ships_by
                }
                */
              }
            } catch(e) {}
            
            return {
              id: v.id,
              quantity: 1,
              properties: item_properties
            }
          })
        }
        
        fetch(window.Shopify.routes.root + 'cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        })
        .then(response => {
          return response.json();
        })
        .then(data => {
          // Handle successful response, e.g., update cart UI
          console.log('Items added to cart:', data);
        })
        .catch((error) => {
          console.error('Error adding items to cart:', error);
        })
        .finally(() => {
          _this.submit_button.classList.remove('loading')
          _this.submit_button.querySelector('.loading__spinner').classList.add('hidden')
        })
        
      }
    }
    
    
    customElements.define('bundle-selections', BundleSelections);

}